// Plant growth beyond the age-crop table (MB.2). PURE (ENG-1): the host drives
// these from random ticks over a narrow name-lookup view. Covers the growth
// styles the M11.1 crop model didn't: vertical stacks (sugar cane / cactus /
// bamboo) that cap at a height, seeded stems that sprout a fruit on a free
// neighbour (melon / pumpkin), and nether wart on soul sand. Every decision is a
// pure function of the world + a single deterministic roll, so growth-rate stats
// and height caps are unit-testable and the sim stays isomorphic.

/** Just enough world for growth decisions: the block name at a cell (null = unloaded). */
export interface GrowthView {
  name(x: number, y: number, z: number): string | null;
}

export interface Cell { readonly x: number; readonly y: number; readonly z: number; }

/** Per-tick growth probability for a vertical plant / stem / nether wart. */
export const GROW_CHANCE = { vertical: 0.1, stem: 0.25, netherWart: 0.1 } as const;

/** Vertical plants and their max stack height + valid supporting blocks. */
export const VERTICAL_PLANTS: Record<string, { cap: number; ground: ReadonlySet<string> }> = {
  sugar_cane: { cap: 3, ground: new Set(['sugar_cane', 'grass_block', 'dirt', 'sand', 'red_sand', 'podzol']) },
  cactus: { cap: 3, ground: new Set(['cactus', 'sand', 'red_sand']) },
  bamboo: { cap: 12, ground: new Set(['bamboo', 'grass_block', 'dirt', 'sand', 'red_sand', 'podzol']) },
};

/** Which fruit each stem sprouts. */
export const STEM_FRUIT: Record<string, string> = { melon_stem: 'melon', pumpkin_stem: 'pumpkin' };

const HORIZ: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const FRUIT_GROUND = new Set(['farmland', 'dirt', 'grass_block', 'coarse_dirt', 'podzol']);

/** Current stacked height of a vertical plant whose top segment is at (x,y,z). */
export function stackHeight(view: GrowthView, x: number, y: number, z: number, block: string): number {
  let h = 1;
  while (view.name(x, y - h, z) === block) h++;
  return h;
}

/**
 * Grow a vertical plant one segment. Only the TOP segment (air directly above)
 * grows, and only while the stack is under its height cap and the roll passes.
 * Returns the air cell to fill, or null (blocked, capped, or no growth this tick).
 */
export function verticalGrow(view: GrowthView, x: number, y: number, z: number, block: string, roll: number): Cell | null {
  const plant = VERTICAL_PLANTS[block];
  if (!plant) return null;
  if (view.name(x, y + 1, z) !== 'air') return null;      // not the top segment / blocked
  if (roll >= GROW_CHANCE.vertical) return null;          // didn't roll growth
  if (stackHeight(view, x, y, z, block) >= plant.cap) return null; // at cap
  return { x, y: y + 1, z };
}

export type StemResult =
  | { kind: 'age'; age: number }
  | { kind: 'fruit'; cell: Cell; fruit: string };

/**
 * Advance a stem. Below maturity it ages one step; at maturity it sprouts its
 * fruit on the first free horizontal neighbour sitting on tillable ground (fixed
 * N/E/S/W order → deterministic). Returns null if it can't grow this tick or has
 * no room to fruit (a fruit already adjacent counts as "no room").
 */
export function stemGrow(view: GrowthView, x: number, y: number, z: number, block: string, age: number, roll: number): StemResult | null {
  const fruit = STEM_FRUIT[block];
  if (!fruit) return null;
  if (roll >= GROW_CHANCE.stem) return null;
  if (age < 7) return { kind: 'age', age: age + 1 };
  for (const [dx, dz] of HORIZ) if (view.name(x + dx, y, z + dz) === fruit) return null; // already fruited
  for (const [dx, dz] of HORIZ) {
    const nx = x + dx, nz = z + dz;
    if (view.name(nx, y, nz) !== 'air') continue;
    if (FRUIT_GROUND.has(view.name(nx, y - 1, nz) ?? '')) return { kind: 'fruit', cell: { x: nx, y, z: nz }, fruit };
  }
  return null;
}

/** Advance nether wart on soul sand (age 0..3, no light needed). Returns the next
 *  age or null (mature, unsupported, or no growth this tick). */
export function netherWartGrow(view: GrowthView, x: number, y: number, z: number, age: number, roll: number): number | null {
  if (age >= 3) return null;
  if (roll >= GROW_CHANCE.netherWart) return null;
  if (view.name(x, y - 1, z) !== 'soul_sand') return null;
  return age + 1;
}

/** Simple age-plants that just ripen in place: cocoa pods (on a jungle log) and
 *  the sweet-berry bush. Value = max age. */
export const AGE_PLANTS: Record<string, number> = { cocoa: 2, sweet_berry_bush: 3 };

/** Advance a ripen-in-place age plant (cocoa/sweet_berry_bush). Returns the next
 *  age or null (mature or no growth this tick). */
export function agePlantGrow(block: string, age: number, roll: number, chance = GROW_CHANCE.stem): number | null {
  const max = AGE_PLANTS[block];
  if (max === undefined || age >= max) return null;
  if (roll >= chance) return null;
  return age + 1;
}
